/**
 * Multi-sport league registry — single source of truth for the frontend.
 *
 * Month indices are JS Date.getMonth() (0-based): Jan=0, Feb=1, … Dec=11.
 * Registry order is also the default-tab priority order for defaultLeague().
 *
 * NOTE: "LIVE" on a tab is only known for the *currently selected* league
 * (it's the only one fetched). Cross-league live badges would require fetching
 * all leagues simultaneously — that is out of scope for v1.
 */

export type LeagueKey =
  | 'soccer-world'
  | 'mlb'
  | 'nba'
  | 'nhl'
  | 'nfl'
  | 'college-football';

interface MonthSeason {
  months: number[];
}

interface TournamentSeason {
  tournament: { start: string; end: string };
}

export interface LeagueConfig {
  key: LeagueKey;
  label: string;
  /** ESPN scoreboard slug, e.g. 'baseball/mlb' */
  espn: string;
  season: MonthSeason | TournamentSeason;
  /**
   * Three-way moneyline (Home/Draw/Away). Soccer can end level in regulation, so
   * its predictions carry a draw outcome; US team sports do not.
   */
  threeWay?: boolean;
}

export const LEAGUES: LeagueConfig[] = [
  {
    key: 'soccer-world',
    label: 'World Cup',
    espn: 'soccer/fifa.world',
    season: { tournament: { start: '2026-06-11', end: '2026-07-19' } },
    threeWay: true,
  },
  {
    key: 'mlb',
    label: 'MLB',
    espn: 'baseball/mlb',
    season: { months: [2, 3, 4, 5, 6, 7, 8, 9] }, // Mar–Oct
  },
  {
    key: 'nba',
    label: 'NBA',
    espn: 'basketball/nba',
    season: { months: [9, 10, 11, 0, 1, 2, 3, 4, 5] }, // Oct–Jun
  },
  {
    key: 'nhl',
    label: 'NHL',
    espn: 'hockey/nhl',
    season: { months: [9, 10, 11, 0, 1, 2, 3, 4, 5] }, // Oct–Jun
  },
  {
    key: 'nfl',
    label: 'NFL',
    espn: 'football/nfl',
    season: { months: [7, 8, 9, 10, 11, 0, 1] }, // Aug–Feb
  },
  {
    key: 'college-football',
    label: 'CFB',
    espn: 'football/college-football',
    season: { months: [7, 8, 9, 10, 11, 0] }, // Aug–Jan
  },
];

/** Whether a league's moneyline is 3-way (Home/Draw/Away), e.g. soccer. */
export function isThreeWay(leagueKey: LeagueKey): boolean {
  return LEAGUES.find((l) => l.key === leagueKey)?.threeWay ?? false;
}

/** Returns true if the given league is in season at the given time. */
export function isInSeason(leagueKey: LeagueKey, now: Date): boolean {
  const league = LEAGUES.find((l) => l.key === leagueKey);
  if (!league) return false;

  const season = league.season;

  if ('months' in season) {
    return season.months.includes(now.getMonth());
  }

  // tournament window: inclusive on both ends.
  // Parse the YYYY-MM-DD bounds as local midnight to match getFullYear/getMonth/getDate
  // on the `now` argument (avoids UTC-vs-local off-by-one on boundary days).
  const [sy, sm, sd] = season.tournament.start.split('-').map(Number);
  const [ey, em, ed] = season.tournament.end.split('-').map(Number);
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startDay = new Date(sy, sm - 1, sd).getTime();
  const endDay = new Date(ey, em - 1, ed).getTime();
  return nowDay >= startDay && nowDay <= endDay;
}

/**
 * Returns the first in-season league by registry priority.
 * Falls back to 'mlb' when no league is in season (longest, broadest season;
 * avoids re-creating the empty-NFL-in-June bug).
 */
export function defaultLeague(now: Date): LeagueKey {
  for (const league of LEAGUES) {
    if (isInSeason(league.key, now)) {
      return league.key;
    }
  }
  return 'mlb';
}
